import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Building2,
  Calendar,
  FileText,
  MessageSquare,
  Send,
  Shield,
  AlertTriangle,
  ListChecks,
  CalendarDays,
  DollarSign
} from 'lucide-react';

// Import all split modules
import { AllProjects } from './project_management/pm_projects';
import { NewTaskModal, TaskDetailsModal, TaskStatusUpdateModal, getStatusBadge, getPriorityBadge } from './project_management/pm_tasks';
import { DailyLogs, getWeatherIcon, getWeatherColor } from './project_management/pm_logs';
import { Documents, DocumentUploadModal, DocumentActionsMenu } from './project_management/pm_documents';
import { RFIs, NewRFIModal, RFIResponseModal, RFIViewDetailsModal, RFIEditModal, RFIActionsMenu } from './project_management/pm_rfis';
import { Submittals, NewSubmittalModal, SubmittalReviewModal, SubmittalViewDetailsModal, SubmittalEditModal, SubmittalActionsMenu } from './project_management/pm_submittals';
import { Inspections, NewInspectionModal, InspectionReportModal, InspectionActionsMenu } from './project_management/pm_inspections';
import { SafetyIncidents } from './project_management/pm_safety';
import { PunchLists } from './project_management/pm_punchlist';
import { ChangeOrders, NewChangeOrderModal, getChangeOrderStatusBadge } from './project_management/pm_change_orders';
import { SchedulesAndTasks } from './project_management/pm_schedules';

// Legacy SchedulesTasks component - replaced by SchedulesAndTasks with DB integration
// Keep for backward compatibility temporarily
export function SchedulesTasks() {
  return <SchedulesAndTasks />;
}

/* Old SchedulesTasks implementation - replaced by pm_schedules.tsx
   Kept temporarily for reference, will be removed in future cleanup
function OldScheduleTasksComponent() {
  const [activeTab, setActiveTab] = useState("timeline");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");

  // Sample data
  const milestones = [
    { id: 1, project: "Residential Complex Alpha", name: "Foundation Complete", boqItem: "CON-001", date: "2024-03-15", status: "completed" },
    { id: 2, project: "Residential Complex Alpha", name: "Structural Framework", boqItem: "STL-001", date: "2024-04-10", status: "in-progress" },
    { id: 3, project: "Hospital Wing Expansion", name: "Site Preparation", boqItem: "EXC-001", date: "2024-03-20", status: "completed" },
    { id: 4, project: "Hospital Wing Expansion", name: "Concrete Pouring", boqItem: "CON-001", date: "2024-04-25", status: "upcoming" },
    { id: 5, project: "Industrial Warehouse", name: "Steel Installation", boqItem: "STL-001", date: "2024-05-15", status: "upcoming" }
  ];

  const tasks = [
    {
      id: 1,
      name: "Foundation Excavation",
      project: "Residential Complex Alpha",
      boqItem: "EXC-001",
      description: "Complete excavation for building foundation according to engineering specifications",
      startDate: "2024-03-01",
      endDate: "2024-03-15",
      expectedOutput: "Excavated area ready for concrete pouring with proper dimensions and soil conditions",
      owner: "John Doe",
      status: "completed",
      priority: "high",
      progress: 100,
      dependencies: "Site survey completion, Permit approval",
      estimatedHours: "120",
      resources: "Excavator, 3 operators, Safety equipment",
      notes: "Weather dependent activity - monitor soil conditions"
    },
    {
      id: 2,
      name: "Concrete Mixing",
      project: "Residential Complex Alpha",
      boqItem: "CON-001",
      description: "Mix and pour concrete for foundation using Grade 25 concrete specifications",
      startDate: "2024-03-16",
      endDate: "2024-03-30",
      expectedOutput: "Foundation concrete poured and cured to required strength",
      owner: "Jane Smith",
      status: "in-progress",
      priority: "high",
      progress: 65,
      dependencies: "Foundation excavation completion",
      estimatedHours: "96",
      resources: "Concrete mixer, Pump truck, 5 workers",
      notes: "Critical path item - monitor weather for pouring schedule"
    },
    {
      id: 3,
      name: "Steel Reinforcement",
      project: "Hospital Wing Expansion",
      boqItem: "STL-001",
      description: "Install steel reinforcement bars according to structural drawings and specifications",
      startDate: "2024-04-01",
      endDate: "2024-04-20",
      expectedOutput: "Steel reinforcement installed and inspected, ready for concrete pour",
      owner: "Mike Johnson",
      status: "not-started",
      priority: "medium",
      progress: 0,
      dependencies: "Foundation formwork completion",
      estimatedHours: "160",
      resources: "Rebar, Tie wire, 4 steel fixers",
      notes: "Requires structural engineer inspection before concrete pour"
    },
    {
      id: 4,
      name: "Formwork Installation",
      project: "Industrial Warehouse",
      boqItem: "FRM-001",
      description: "Set up formwork for concrete columns and beams as per architectural drawings",
      startDate: "2024-04-15",
      endDate: "2024-05-05",
      expectedOutput: "Formwork installed, aligned, and braced ready for concrete placement",
      owner: "Sarah Wilson",
      status: "upcoming",
      priority: "high",
      progress: 0,
      dependencies: "Rebar installation completion",
      estimatedHours: "200",
      resources: "Formwork panels, Props, 6 carpenters",
      notes: "Ensure proper alignment and leveling - critical for structural integrity"
    },
    {
      id: 5,
      name: "Quality Inspection",
      project: "Residential Complex Alpha",
      boqItem: "MSN-001",
      description: "Conduct comprehensive quality inspection of masonry work",
      startDate: "2024-03-10",
      endDate: "2024-03-12",
      expectedOutput: "Approved masonry work quality report with recommendations",
      owner: "Alex Brown",
      status: "overdue",
      priority: "critical",
      progress: 80,
      dependencies: "Masonry work completion",
      estimatedHours: "16",
      resources: "Inspection tools, Testing equipment",
      notes: "URGENT: Overdue inspection blocking subsequent work"
    }
  ];

  // Calendar sample events
  const calendarEvents = [
    { id: 1, title: "Foundation Excavation", start: "2024-03-01", end: "2024-03-15", project: "Residential Complex Alpha" },
    { id: 2, title: "Concrete Mixing", start: "2024-03-16", end: "2024-03-30", project: "Residential Complex Alpha" },
    { id: 3, title: "Steel Reinforcement", start: "2024-04-01", end: "2024-04-20", project: "Hospital Wing Expansion" },
    { id: 4, title: "Formwork Setup", start: "2024-04-15", end: "2024-05-05", project: "Industrial Warehouse" }
  ];

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         task.project.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         task.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || task.status === statusFilter;
    const matchesOwner = ownerFilter === "all" || task.owner === ownerFilter;
    return matchesSearch && matchesStatus && matchesOwner;
  });

  const getScheduleStatusBadge = (status: string) => {
    switch (status) {
      case "completed": return <Badge variant="default" className="bg-green-600">Completed</Badge>;
      case "in-progress": return <Badge variant="default">In Progress</Badge>;
      case "not-started": return <Badge variant="secondary">Not Started</Badge>;
      case "overdue": return <Badge variant="destructive">Overdue</Badge>;
      case "upcoming": return <Badge variant="outline">Upcoming</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getSchedulePriorityBadge = (priority: string) => {
    switch (priority) {
      case "critical": return <Badge variant="destructive">Critical</Badge>;
      case "high": return <Badge className="bg-orange-600">High</Badge>;
      case "medium": return <Badge variant="secondary">Medium</Badge>;
      case "low": return <Badge variant="outline">Low</Badge>;
      default: return <Badge variant="outline">{priority}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Schedules & Tasks</h2>
          <p className="text-muted-foreground">Project schedules, milestones, and task management</p>
        </div>
        <NewTaskModal />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <CardSchedule className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeaderSchedule className="pb-3">
            <CardTitleSchedule className="text-sm font-medium flex items-center gap-2">
              <Play className="h-4 w-4 text-blue-600" />
              Active Tasks
            </CardTitleSchedule>
          </CardHeaderSchedule>
          <CardContentSchedule>
            <div className="text-2xl font-bold text-blue-700">{tasks.filter(t => t.status === 'in-progress').length}</div>
            <p className="text-xs text-blue-600 mt-1">Currently in progress</p>
          </CardContentSchedule>
        </CardSchedule>

        <CardSchedule className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeaderSchedule className="pb-3">
            <CardTitleSchedule className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Completed Tasks
            </CardTitleSchedule>
          </CardHeaderSchedule>
          <CardContentSchedule>
            <div className="text-2xl font-bold text-green-700">{tasks.filter(t => t.status === 'completed').length}</div>
            <p className="text-xs text-green-600 mt-1">Tasks finished</p>
          </CardContentSchedule>
        </CardSchedule>

        <CardSchedule className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardHeaderSchedule className="pb-3">
            <CardTitleSchedule className="text-sm font-medium flex items-center gap-2">
              <AlertTriangleIcon className="h-4 w-4 text-red-600" />
              Overdue Tasks
            </CardTitleSchedule>
          </CardHeaderSchedule>
          <CardContentSchedule>
            <div className="text-2xl font-bold text-red-700">{tasks.filter(t => t.status === 'overdue').length}</div>
            <p className="text-xs text-red-600 mt-1">Need immediate attention</p>
          </CardContentSchedule>
        </CardSchedule>

        <CardSchedule className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeaderSchedule className="pb-3">
            <CardTitleSchedule className="text-sm font-medium flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-purple-600" />
              Upcoming Milestones
            </CardTitleSchedule>
          </CardHeaderSchedule>
          <CardContentSchedule>
            <div className="text-2xl font-bold text-purple-700">{milestones.filter(m => m.status === 'upcoming').length}</div>
            <p className="text-xs text-purple-600 mt-1">Next 30 days</p>
          </CardContentSchedule>
        </CardSchedule>
      </div>

      <CardSchedule>
        <CardHeaderSchedule>
          <CardTitleSchedule className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5" />
            Schedule Management
          </CardTitleSchedule>
          <CardDescriptionSchedule>Manage project timelines, milestones, and tasks</CardDescriptionSchedule>
        </CardHeaderSchedule>
        <CardContentSchedule>
          <TabsSchedule value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsListSchedule className="grid w-full grid-cols-3">
              <TabsTriggerSchedule value="timeline">Project Timeline</TabsTriggerSchedule>
              <TabsTriggerSchedule value="tasks">Task List</TabsTriggerSchedule>
              <TabsTriggerSchedule value="calendar">Calendar View</TabsTriggerSchedule>
            </TabsListSchedule>

            <TabsContentSchedule value="timeline" className="space-y-4">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Project Milestones</h3>
                <div className="space-y-3">
                  {milestones.map((milestone) => (
                    <div key={milestone.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className={`w-3 h-3 rounded-full ${
                          milestone.status === 'completed' ? 'bg-green-600' :
                          milestone.status === 'in-progress' ? 'bg-blue-600' :
                          'bg-gray-300'
                        }`} />
                        <div>
                          <h4 className="font-medium">{milestone.name}</h4>
                          <p className="text-sm text-muted-foreground">
                            {milestone.project} • {milestone.boqItem}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground">{milestone.date}</span>
                        {getScheduleStatusBadge(milestone.status)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContentSchedule>

            <TabsContentSchedule value="tasks" className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search tasks, projects, or descriptions..."
                    className="pl-9"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="not-started">Not Started</SelectItem>
                    <SelectItem value="in-progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Owners</SelectItem>
                    <SelectItem value="John Doe">John Doe</SelectItem>
                    <SelectItem value="Jane Smith">Jane Smith</SelectItem>
                    <SelectItem value="Mike Johnson">Mike Johnson</SelectItem>
                    <SelectItem value="Sarah Wilson">Sarah Wilson</SelectItem>
                    <SelectItem value="Alex Brown">Alex Brown</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                {filteredTasks.map((task) => (
                  <div key={task.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-medium">{task.name}</h4>
                          {getScheduleStatusBadge(task.status)}
                          {getSchedulePriorityBadge(task.priority)}
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">{task.description}</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="font-medium">Project:</span>
                            <p className="text-muted-foreground">{task.project}</p>
                          </div>
                          <div>
                            <span className="font-medium">BOQ Item:</span>
                            <p className="text-muted-foreground">{task.boqItem}</p>
                          </div>
                          <div>
                            <span className="font-medium">Owner:</span>
                            <p className="text-muted-foreground">{task.owner}</p>
                          </div>
                          <div>
                            <span className="font-medium">Progress:</span>
                            <p className="text-muted-foreground">{task.progress}%</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <TaskDetailsModal mode="view" task={task} trigger={
                          <Button variant="outline" size="sm">View</Button>
                        } />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filteredTasks.length === 0 && (
                <div className="text-center py-12">
                  <CalendarDays className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No tasks found</h3>
                  <p className="text-muted-foreground">
                    {searchQuery || statusFilter !== "all" || ownerFilter !== "all"
                      ? "Try adjusting your search criteria or filters"
                      : "No tasks available"
                    }
                  </p>
                </div>
              )}
            </TabsContentSchedule>

            <TabsContentSchedule value="calendar" className="space-y-4">
              <div className="text-center py-12">
                <Calendar className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Calendar View</h3>
                <p className="text-muted-foreground">Interactive calendar view coming soon</p>
              </div>
            </TabsContentSchedule>
          </TabsSchedule>
        </CardContentSchedule>
      </CardSchedule>
    </div>
  );
}
End of old implementation */

// Main ProjectManagement Component with Tab Navigation
export default function ProjectManagement() {
  const [activeTab, setActiveTab] = useState("projects");

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Project Management</h1>
        <p className="text-muted-foreground">Comprehensive project management and collaboration tools</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5 lg:grid-cols-10">
          <TabsTrigger value="projects" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            <span className="hidden md:inline">Projects</span>
          </TabsTrigger>
          <TabsTrigger value="schedules" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span className="hidden md:inline">Schedules</span>
          </TabsTrigger>
          <TabsTrigger value="dailylogs" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <span className="hidden md:inline">Daily Logs</span>
          </TabsTrigger>
          <TabsTrigger value="documents" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <span className="hidden md:inline">Documents</span>
          </TabsTrigger>
          <TabsTrigger value="rfis" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            <span className="hidden md:inline">RFIs</span>
          </TabsTrigger>
          <TabsTrigger value="submittals" className="flex items-center gap-2">
            <Send className="h-4 w-4" />
            <span className="hidden md:inline">Submittals</span>
          </TabsTrigger>
          <TabsTrigger value="inspections" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            <span className="hidden md:inline">Inspections</span>
          </TabsTrigger>
          <TabsTrigger value="safety" className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            <span className="hidden md:inline">Safety</span>
          </TabsTrigger>
          <TabsTrigger value="punchlist" className="flex items-center gap-2">
            <ListChecks className="h-4 w-4" />
            <span className="hidden md:inline">Punch List</span>
          </TabsTrigger>
          <TabsTrigger value="changeorders" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            <span className="hidden md:inline">Change Orders</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="projects" className="mt-6">
          <AllProjects />
        </TabsContent>

        <TabsContent value="schedules" className="mt-6">
          <SchedulesTasks />
        </TabsContent>

        <TabsContent value="dailylogs" className="mt-6">
          <DailyLogs />
        </TabsContent>

        <TabsContent value="documents" className="mt-6">
          <Documents />
        </TabsContent>

        <TabsContent value="rfis" className="mt-6">
          <RFIs />
        </TabsContent>

        <TabsContent value="submittals" className="mt-6">
          <Submittals />
        </TabsContent>

        <TabsContent value="inspections" className="mt-6">
          <Inspections />
        </TabsContent>

        <TabsContent value="safety" className="mt-6">
          <SafetyIncidents />
        </TabsContent>

        <TabsContent value="punchlist" className="mt-6">
          <PunchLists />
        </TabsContent>

        <TabsContent value="changeorders" className="mt-6">
          <ChangeOrders />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Re-export all components for backward compatibility
export {
  AllProjects,
  DailyLogs,
  Documents,
  RFIs,
  Submittals,
  Inspections,
  SafetyIncidents,
  PunchLists,
  ChangeOrders,
  // Also export utility functions and modals that might be used elsewhere
  NewTaskModal,
  TaskDetailsModal,
  TaskStatusUpdateModal,
  getStatusBadge,
  getPriorityBadge,
  getWeatherIcon,
  getWeatherColor,
  DocumentUploadModal,
  DocumentActionsMenu,
  NewRFIModal,
  RFIResponseModal,
  RFIViewDetailsModal,
  RFIEditModal,
  RFIActionsMenu,
  NewSubmittalModal,
  SubmittalReviewModal,
  SubmittalViewDetailsModal,
  SubmittalEditModal,
  SubmittalActionsMenu,
  NewInspectionModal,
  InspectionReportModal,
  InspectionActionsMenu,
  NewChangeOrderModal,
  getChangeOrderStatusBadge
};
